import { PageModel } from "../../types/PageModel";
import { readFragment } from "gql.tada";
import { useTheme } from "@hooks/useTheme";
import { useLayoutEffect, useMemo } from "react";
import { ThemeFragment } from "@queries/fragments/ThemeFragment";
import { isValidTheme } from "../../types/ThemeModel";
import { defaultTheme } from "@utils/ThemeContext";
import { PageContext } from "@utils/PageContext";
import { MetadataHelmet } from "./MetadataHelmet";
import { useScrollReset } from "@hooks/useScrollReset";
import { HomepageRenderer } from "@components/archetypes/HomepageRenderer";
import { ProductPageRenderer } from "@components/archetypes/ProductPageRenderer";
import { EditorialPageRenderer } from "@components/archetypes/EditorialPageRenderer";
import { ContentHubPageRenderer } from "@components/archetypes/ContentHubPageRenderer";
import { ConversionPageRenderer } from "@components/archetypes/ConversionPageRenderer";
import { FaqPageRenderer } from "@components/archetypes/FaqPageRenderer";
import { TermsPageRenderer } from "@components/archetypes/TermsPageRenderer";
import { CreativeSpecsPageRenderer } from "@components/archetypes/CreativeSpecsPageRenderer";
import { CaseStudyHomepageRenderer } from "@components/archetypes/CaseStudyHomepageRenderer";

const ArchetypeRenderer = ({ archetype }: { archetype: PageModel["archetype"] }) => {
  switch (archetype.__typename) {
    case "Homepage":
      return <HomepageRenderer data={archetype.data} />;
    case "ProductPage":
      return <ProductPageRenderer data={archetype.data} />;
    case "EditorialPage":
      return <EditorialPageRenderer data={archetype.data} />;
    case "ContentHubPage":
      return <ContentHubPageRenderer data={archetype.data} />;
    case "ConversionPage":
      return <ConversionPageRenderer data={archetype.data} />;
    case "FaqPage":
      return <FaqPageRenderer data={archetype.data} />;
    case "TermsPage":
      return <TermsPageRenderer data={archetype.data} />;
    case "CreativeSpecsPage":
      return <CreativeSpecsPageRenderer data={archetype.data} />;
    case "CaseStudyHomepage":
      return <CaseStudyHomepageRenderer data={archetype.data} />;
  }
};

export const Page = ({ page }: { page: PageModel }) => {
  useScrollReset();

  const { setTheme } = useTheme();
  const theme = useMemo(
    () => readFragment(ThemeFragment, page.pageInfo.theme) ?? defaultTheme,
    [page.pageInfo.theme]
  );

  useLayoutEffect(() => {
    if (!isValidTheme(theme)) {
      console.warn(`Palette combination invalid: `, theme);
      return;
    }

    setTheme(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  return (
    <PageContext.Provider value={page}>
      <MetadataHelmet pageInfo={page.pageInfo} />
      <ArchetypeRenderer archetype={page.archetype} />
    </PageContext.Provider>
  );
};
